import React, { useState, useEffect } from "react";
import {
  Autocomplete,
  Box,
  createTheme,
  Modal,
  Paper,
  ThemeProvider,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { useSelector } from "react-redux";
import { Network } from "@capacitor/network";

import cssClasses from "./LeadSourceChange.module.css";
import Close from "../../Components/Icons/Close";
import Button from "../../Subcomponents/Button";
import DropDown from "../../Subcomponents/DropDown";
import ConfirmationAlert from "../../Subcomponents/ConfirmationAlert";
import SearchIcon from "../../Assets/Images/Common/SearchIcon.svg";

import { errorTost, offlineToast, successTost } from "../../Services/Toast";
import { presentLoader, dismissLoader } from "../../Services/loader";
import { getAllCpForLaunch, getAllFOSUnderCP } from "../../Services/ChannelPartner";
import {
  addLeadLogs,
  updateLeadCpId,
  checkLeadHasApplicationApiRes,
  getAllotmentDetailsForLeadSourceChange,
  getLeadDetailsForFosWalletAssignment,
} from "../../Services/Lead";
import { deleteCpPaymentEntry, getCpPaymentGateStatus } from "../../Services/CpPayout";
import { getApplicableBasicBrokeragePercentage } from "../../Services/Brokerage";
import {
  fetchBookingBrokerageData,
  deleteFarvisionBrokerLines,
  createFarvisionBrokerLine,
  restoreFarvisionBrokerLines,
} from "../../Services/FarvisionBrokerChange";
import {
  switchFosToFos as switchFosWalletToFos,
  assignFosFirstTime as assignFosWalletFirstTime,
  removeFosOnSwitchToDirect as removeFosWalletOnSwitchToDirect,
  checkFosSwitchEligibility as checkFosWalletSwitchEligibility,
} from "../../Services/FosWalletChange";

const LEAD_TYPE_OPTIONS = [
  { label: "Direct", value: "Direct" },
  { label: "CP", value: "CP" },
  { label: "R&L", value: "R&L" },
];

const autocompleteTheme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: "1.6rem !important",
          fontFamily: "Inter-Medium !important",
          lineHeight: "2.4rem !important",
          borderRadius: 8,
          "& .MuiOutlinedInput-notchedOutline": { border: "none" },
          "&.Mui-focused": {
            background: "#FFFFFF",
            border: "1px solid #84CAFF !important",
            boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #D1E9FF",
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: { "& label": { fontSize: "1.6rem !important" } },
      },
    },
  },
});

export default function LeadSourceChange({ open, handleClose, data, returnRes }) {
  const loggedInUser = useSelector((state) => state.Auth.user);

  const [selectedLeadType, setSelectedLeadType] = useState("");

  const [allCPs, setAllCPs] = useState([]);
  const [selectedCPName, setSelectedCPName] = useState("");
  const [selectedCPData, setSelectedCPData] = useState(null);
  const [cpHelperText, setCpHelperText] = useState("");

  const [allFOS, setAllFOS] = useState([]);
  const [selectedFOSName, setSelectedFOSName] = useState("");
  const [selectedFOSData, setSelectedFOSData] = useState(null);
  const [fosLoading, setFosLoading] = useState(false);
  const [fosHelperText, setFosHelperText] = useState("");

  const [notes, setNotes] = useState("");
  const [hasApplicationApiRes, setHasApplicationApiRes] = useState(false);
  const [showFosConfirm, setShowFosConfirm] = useState(false);
  const [allotmentDetails, setAllotmentDetails] = useState(null);
  // Distinct from allotmentDetails === null: that's the normal "no allotment
  // generated yet" state (safe to proceed, nothing to gate — see submitUpdate).
  // This flag is only set when the fetch itself errored, which must still
  // block — a lead that DOES have an allotment/CP-payment obligation but
  // whose check failed transiently must not silently skip the gate.
  const [allotmentFetchFailed, setAllotmentFetchFailed] = useState(false);
  // Starts true, flips false once the allotment fetch below settles (success
  // OR failure). Needed because allotmentDetails===null is ambiguous between
  // "confirmed no allotment" and "fetch still in flight" — without this, a
  // fast click on Update right after the modal opens could race the fetch
  // and silently skip Farvision/CP-gate/wallet (same as the no-allotment
  // case) even on a lead that DOES have a real allotment.
  const [allotmentDetailsLoading, setAllotmentDetailsLoading] = useState(true);

  const [processing, setProcessing] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [resultIsError, setResultIsError] = useState(false);

  const existingCPName = data?.CP?.name !== "-" ? data?.CP?.name : null;
  const existingFOSName = data?.FOS?.name !== "-" ? data?.FOS?.name : null;
  const existingFOSId = data?.FOS?.fosId !== "-" ? data?.FOS?.fosId : null;
  const existingCPId = data?.CP?.cpId !== "-" ? data?.CP?.cpId : null;
  const existingLeadType = data?.type || "-";

  const isDirect = selectedLeadType === "Direct";
  const isCP = selectedLeadType === "CP";

  // Which of the 3 spec-covered transitions this change is — only these get
  // the payment gate / Farvision sync / processing UI. Anything else (e.g.
  // R&L involved) falls back to the original simple update.
  const isCpToDirect = existingLeadType === "CP" && selectedLeadType === "Direct";
  const isDirectToCp = existingLeadType === "Direct" && selectedLeadType === "CP";
  const isCpToCp = existingLeadType === "CP" && selectedLeadType === "CP";
  const usesGatedFlow = isCpToDirect || isDirectToCp || isCpToCp;

  useEffect(() => {
    Network.getStatus().then((status) => {
      if (status.connected) {
        getAllCpForLaunch("")
          .then((resp) => setAllCPs(resp.data || []))
          .catch((err) => {
            console.error(err);
            errorTost("Failed to load CP list");
          });
        checkLeadHasApplicationApiRes(data.leadId)
          .then((result) => setHasApplicationApiRes(result))
          .catch((err) => console.error("applicationApiRes check failed", err));
        getAllotmentDetailsForLeadSourceChange(data.leadId)
          .then((result) => setAllotmentDetails(result))
          .catch((err) => {
            console.error("allotment details fetch failed", err);
            setAllotmentFetchFailed(true);
          })
          .finally(() => setAllotmentDetailsLoading(false));
      } else {
        offlineToast();
      }
    });
  }, []);

  function handleLeadTypeChange(value) {
    setSelectedLeadType(value);
    // Reset CP/FOS when switching type
    setSelectedCPName("");
    setSelectedCPData(null);
    setCpHelperText("");
    setAllFOS([]);
    setSelectedFOSName("");
    setSelectedFOSData(null);
    setFosHelperText("");
  }

  function handleCPSelect(newValue) {
    setAllFOS([]);
    setSelectedFOSName("");
    setSelectedFOSData(null);
    setFosHelperText("");

    if (newValue) {
      setCpHelperText("");
      setSelectedCPName(newValue.label);
      const found = allCPs.find((cp) => cp.cpId === newValue.value);
      setSelectedCPData(found || null);
      if (found) {
        setFosLoading(true);
        getAllFOSUnderCP(found.cpId)
          .then((resp) => setAllFOS(resp.data || []))
          .catch((err) => {
            console.error(err);
            errorTost("Failed to load FOS list");
          })
          .finally(() => setFosLoading(false));
      }
    } else {
      setSelectedCPName("");
      setSelectedCPData(null);
    }
  }

  function handleUpdate() {
    if (!selectedLeadType) {
      errorTost("Please select a lead type");
      return;
    }
    if (isCP && !selectedCPData) {
      setCpHelperText("Please select a CP to update");
      return;
    }
    if (!notes.trim()) {
      errorTost("Please add a note before updating");
      return;
    }

    if (isCP && allFOS.length > 0 && !selectedFOSData) {
      setShowFosConfirm(true);
      return;
    }

    submitUpdate();
  }

  // Shared final step for both flows — the existing Leads write + audit log.
  async function performLeadUpdateAndLog() {
    const newCpId = isCP ? selectedCPData.cpId : null;
    const newFosId = isCP ? (selectedFOSData?.fosId || null) : null;

    await updateLeadCpId(data.leadId, newCpId, newFosId, selectedLeadType);

    const logObj = {
      cpId: isDirect ? null : selectedCPData.cpId,
      fosId: isDirect ? null : (selectedFOSData?.fosId || null),
      userId: loggedInUser.userId,
      leadId: data.leadId,
      leadStatus: "Lead Source Changed",
      message: isCP
        ? `Lead source changed${existingCPName ? ` from CP: ${existingCPName}, FOS: ${existingFOSName || "NA"}` : ""} to CP: ${selectedCPData.billingName}, FOS: ${selectedFOSData ? selectedFOSData.name : "NA"} by ${loggedInUser.name}(${loggedInUser.roleId.title})`
        : `Lead type changed to ${selectedLeadType}${existingCPName ? ` from CP: ${existingCPName}, FOS: ${existingFOSName || "NA"}` : ""} by ${loggedInUser.name}(${loggedInUser.roleId.title}). CP and FOS removed.`,
      Note: notes.trim(),
    };
    addLeadLogs(logObj).catch((err) => console.error("leadlog", err));
  }

  // FOS-wallet ELIGIBILITY CHECK — read-only, no mutation. Runs first, before
  // the CP-payment gate and any Farvision call, so a debited allotment is
  // blocked before anything else runs. The actual mutation is
  // applyFosWalletChange() below, called only after Farvision has already
  // succeeded — so a blocked/failed check here never leaves Farvision or the
  // wallet touched.
  async function checkFosWalletEligibility() {
    const newFosId = isCP ? (selectedFOSData?.fosId || null) : null;

    if (existingFOSId === newFosId) {
      return { success: true };
    }
    if (!allotmentDetails?.allotmentPaymentId) {
      return { success: true };
    }
    // Only switching away from an existing FOS (to a different FOS, or to
    // none) can ever be blocked by a prior debit. Assigning a FOS for the
    // first time has nothing to protect.
    if (!existingFOSId) {
      return { success: true };
    }

    try {
      const result = await checkFosWalletSwitchEligibility(allotmentDetails.allotmentPaymentId);
      if (result.hasActiveDebit) {
        return {
          success: false,
          message: "Reward points for this allotment have already been debited to the current FOS — cannot change FOS.",
        };
      }
      return { success: true };
    } catch (err) {
      console.error("FOS wallet eligibility check failed:", err);
      return { success: false, message: err.message || "FOS wallet check failed." };
    }
  }

  // FOS-wallet APPLY — the actual mutation. Called only after the CP-payment
  // gate and Farvision have already succeeded: checkFosWalletEligibility()
  // above already refused a debited allotment before either of those ran, so
  // by the time this runs the only remaining failure mode is a rare
  // network/DB error on the wallet call itself — Farvision has already
  // moved, so that failure is surfaced explicitly as needing manual
  // reconciliation rather than silently leaving mismatched state.
  async function applyFosWalletChange() {
    const newFosId = isCP ? (selectedFOSData?.fosId || null) : null;

    if (existingFOSId === newFosId) {
      return { success: true };
    }
    if (!allotmentDetails?.allotmentPaymentId) {
      return { success: true };
    }

    try {
      if (existingFOSId && newFosId) {
        await switchFosWalletToFos(allotmentDetails.allotmentPaymentId, newFosId);
      } else if (!existingFOSId && newFosId) {
        const leadDetails = await getLeadDetailsForFosWalletAssignment(data.leadId);
        await assignFosWalletFirstTime(
          data.leadId,
          newFosId,
          {
            name: leadDetails?.name || data?.leadname || "",
            mobile: leadDetails?.mobile || "",
            email: leadDetails?.email ?? "",
            // fos_leads.unit_id is the Farvision numeric unit id, not
            // SalesApp's own unitId UUID — matches the canonical FOS sync
            // (fosWalletApi.js), which populates it from Inventory.farvisionUnitId.
            unit_id: allotmentDetails.farvisionUnitId,
            project_id: allotmentDetails.projectId,
            status: allotmentDetails.status,
            booking_id: allotmentDetails.bookingId,
            sales_app_lead_display_id: leadDetails?.sales_app_lead_display_id || null,
          },
          {
            allotmentId: allotmentDetails.allotmentPaymentId,
            unitCost: allotmentDetails.unitCost,
            status: allotmentDetails.status,
            unitNo: allotmentDetails.unitNo,
            wingName: allotmentDetails.wingName,
            projectName: allotmentDetails.projectName,
            farvisionUnitId: allotmentDetails.farvisionUnitId,
            projectId: allotmentDetails.projectId,
            farvisionBuId: allotmentDetails.buId,
            bookingId: allotmentDetails.bookingId,
          }
        );
      } else if (existingFOSId && !newFosId) {
        await removeFosWalletOnSwitchToDirect(allotmentDetails.allotmentPaymentId);
      }
      return { success: true };
    } catch (err) {
      console.error("FOS wallet apply failed:", err);
      return {
        success: false,
        message:
          (err.message || "FOS wallet update failed.") +
          " — Farvision brokerage was already moved, but the FOS wallet and lead record were not. This needs manual reconciliation.",
      };
    }
  }

  // Runs the full gated flow silently (no intermediate UI updates) and
  // resolves to a single { success, message } result for the caller to show.
  async function runGatedFlow() {
    const eligibility = await checkFosWalletEligibility();
    if (!eligibility.success) {
      return { success: false, message: eligibility.message };
    }

    // Direct→CP has no prior CP payment to gate — just Farvision + update.
    if (isDirectToCp) {
      try {
        if (allotmentDetails?.bookingId) {
          const brokeragePercentage = await getApplicableBasicBrokeragePercentage(
            allotmentDetails.projectId,
            allotmentDetails.wingId
          );
          await createFarvisionBrokerLine({
            bookingId: allotmentDetails.bookingId,
            buId: allotmentDetails.buId,
            newCpLedgerId: selectedCPData.crmLedgerDetails?.id,
            brokeragePercentage,
            agreementValue: allotmentDetails.agreementValue,
          });
        }
      } catch (err) {
        console.error(err);
        return { success: false, message: err.message || "Farvision call failed." };
      }

      const applyResult = await applyFosWalletChange();
      if (!applyResult.success) {
        return { success: false, message: applyResult.message };
      }

      await performLeadUpdateAndLog();
      return { success: true, message: "Lead source updated successfully." };
    }

    // CP→Direct / CP→CP — payment gate first.
    let gate;
    try {
      gate = await getCpPaymentGateStatus(allotmentDetails?.allotmentPaymentId);
    } catch (err) {
      console.error(err);
      return { success: false, message: "Failed to check CP payment status. Please try again." };
    }

    if (gate.action === "block" || gate.action === "block_permanent") {
      return { success: false, message: gate.message };
    }

    if (gate.action === "auto_delete") {
      const delRes = await deleteCpPaymentEntry(allotmentDetails.allotmentPaymentId, notes.trim());
      if (!delRes.success) {
        return { success: false, message: delRes.message || "Failed to delete the existing CP payment entry." };
      }
    }

    // Farvision — delete old CP's line, and for CP→CP, create the new CP's line too.
    try {
      if (allotmentDetails?.bookingId) {
        if (isCpToCp && !selectedCPData?.crmLedgerDetails?.id) {
          // Fail before touching Farvision at all — no point deleting the old
          // CP's line just to immediately roll it back.
          throw new Error("The selected CP has no Farvision CRM ledger id on file — cannot move brokerage to them.");
        }
        if (isCpToCp) {
          // Fetch once and share it — deleting the old line first would otherwise
          // consume the only template the create step has left to copy from.
          const preFetched = await fetchBookingBrokerageData(allotmentDetails.bookingId, allotmentDetails.buId);
          await deleteFarvisionBrokerLines(allotmentDetails.bookingId, allotmentDetails.buId, preFetched);

          // Delete succeeded — from here on, any failure must roll the delete
          // back so the booking never ends up with zero brokerage lines.
          try {
            const brokeragePercentage = await getApplicableBasicBrokeragePercentage(
              allotmentDetails.projectId,
              allotmentDetails.wingId
            );
            await createFarvisionBrokerLine({
              bookingId: allotmentDetails.bookingId,
              buId: allotmentDetails.buId,
              newCpLedgerId: selectedCPData.crmLedgerDetails?.id,
              brokeragePercentage,
              agreementValue: allotmentDetails.agreementValue,
              preFetched,
            });
          } catch (createErr) {
            console.error("create failed after delete — rolling back", createErr);
            let rollbackError = null;
            try {
              await restoreFarvisionBrokerLines(preFetched);
            } catch (restoreErr) {
              console.error("rollback also failed", restoreErr);
              rollbackError = restoreErr;
            }
            if (rollbackError) {
              throw new Error(
                `${createErr.message} — AND restoring the old CP's brokerage line failed too (${rollbackError.message}). This booking may now have no brokerage line in Farvision — needs manual fix.`
              );
            }
            throw new Error(`${createErr.message} — old CP's brokerage line was restored in Farvision.`);
          }
        } else {
          await deleteFarvisionBrokerLines(allotmentDetails.bookingId, allotmentDetails.buId);
        }
      }
    } catch (err) {
      console.error(err);
      return { success: false, message: err.message || "Farvision call failed." };
    }

    const applyResult = await applyFosWalletChange();
    if (!applyResult.success) {
      return { success: false, message: applyResult.message };
    }

    await performLeadUpdateAndLog();
    return { success: true, message: "Lead source updated successfully." };
  }

  function submitUpdate() {
    Network.getStatus().then((status) => {
      if (!status.connected) {
        offlineToast();
        return;
      }

      if (!usesGatedFlow) {
        presentLoader();
        performLeadUpdateAndLog()
          .then(() => {
            successTost("Lead source updated successfully");
            returnRes(true);
            handleClose();
          })
          .catch((err) => {
            console.error(err);
            errorTost("Failed to update lead source");
          })
          .finally(() => dismissLoader());
        return;
      }

      // Must not proceed while the allotment fetch is still in flight —
      // allotmentDetails===null is ambiguous between "confirmed no
      // allotment" and "fetch not finished yet", and only this flag tells
      // them apart. Racing ahead here is exactly how a real allotment's
      // Farvision/CP-gate/wallet steps get silently skipped.
      if (allotmentDetailsLoading) {
        errorTost("Still loading allotment details — please wait a moment and try again.");
        return;
      }

      // A genuinely failed fetch must still block — unlike the "no allotment
      // generated yet" case (allotmentDetails === null with no fetch error),
      // which runGatedFlow() already handles safely at every step: the FOS
      // wallet check/apply both no-op without an allotmentPaymentId, the CP
      // payment gate returns "none" for a missing allotmentPaymentId, and
      // every Farvision call is already guarded on allotmentDetails?.bookingId.
      if (allotmentFetchFailed) {
        errorTost("Failed to load allotment details — please try again.");
        return;
      }

      setProcessing(true);
      setResultMessage("");
      setResultIsError(false);
      runGatedFlow().then((result) => {
        setResultMessage(result.message);
        setResultIsError(!result.success);
        if (result.success) returnRes(true);
      });
    });
  }

  return (
    <>
    <Modal
      open={open && !(resultMessage && resultIsError)}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      aria-labelledby="lead-source-change-modal"
      sx={{ ".MuiBackdrop-root": { background: "rgba(52, 64, 84, 0.7)" } }}
      disableAutoFocus={true}
    >
      <Box className={cssClasses.modalWrapper}>
        {/* Header */}
        <div className={cssClasses.wrapperHeader}>
          <span className={cssClasses.closeIcon} onClick={handleClose} tabIndex={0}>
            <Close width={"1.2rem"} height={"1.2rem"} />
          </span>
          Lead Source Change
        </div>

        <div className={cssClasses.wrapperContent}>
        {processing ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "1.6rem",
              padding: "4rem 1rem",
              textAlign: "center",
            }}
          >
            {resultMessage && !resultIsError ? (
              <div
                style={{
                  width: "100%",
                  background: "#ECFDF3",
                  border: "1px solid #ABEFC6",
                  borderRadius: "8px",
                  padding: "1.2rem 1.6rem",
                  color: "#067647",
                  fontSize: "1.4rem",
                  fontFamily: "Inter-Medium",
                  textAlign: "left",
                }}
              >
                {resultMessage}
              </div>
            ) : !resultMessage ? (
              <>
                <div className={cssClasses.spinner} />
                <div style={{ fontFamily: "Inter-Medium", fontSize: "1.4rem", color: "#667085" }}>
                  Updating lead source...
                </div>
              </>
            ) : null}
          </div>
        ) : (
        <>
          {hasApplicationApiRes && (
            <div style={{
              background: "#FFFAEB",
              border: "1px solid #FEC84B",
              borderRadius: "8px",
              padding: "1.2rem 1.6rem",
              color: "#B54708",
              fontSize: "1.4rem",
              fontFamily: "Inter-Medium",
              marginBottom: "1.6rem",
            }}>
              Application has already been submitted to CRM for this lead. Changing the lead source now will not roll back that application — please confirm this is intended before proceeding.
            </div>
          )}
          {/* Lead Type row — current value + dropdown to change */}
          <div className={cssClasses.leadTypeRow}>
            <div className={cssClasses.currentBlock}>
              <div className={cssClasses.sectionLabel}>Current Lead Type</div>
              <div className={cssClasses.currentValue}>{existingLeadType}</div>
            </div>
            <div className={cssClasses.currentBlock}>
              <div className={cssClasses.sectionLabel}>
                Change Lead Type <span style={{ color: "red" }}>*</span>
              </div>
              <DropDown
                list={LEAD_TYPE_OPTIONS.filter((opt) =>
                  opt.value !== "R&L" &&
                  !(existingLeadType === "Direct" && opt.value === "Direct")
                )}
                selectedValue={selectedLeadType}
                onChangeSelect={handleLeadTypeChange}
                placeHolder="Select type"
                style={{ background: "#F2F4F7", color: "#344054" }}
              />
            </div>
          </div>

          {/* Current CP & FOS — only show if existing lead type is CP */}
          {existingLeadType !== "Direct" && existingLeadType !== "R&L" && (
            <div className={cssClasses.currentRow}>
              <div className={cssClasses.currentBlock}>
                <div className={cssClasses.sectionLabel}>Current CP</div>
                <div className={cssClasses.currentValue}>{existingCPName || "No CP assigned"}</div>
              </div>
              <div className={cssClasses.currentBlock}>
                <div className={cssClasses.sectionLabel}>Current FOS</div>
                <div className={cssClasses.currentValue}>{existingFOSName || "No FOS assigned"}</div>
              </div>
            </div>
          )}

          {/* CP & FOS selection — only for CP lead type */}
          {isCP && (
            <>
              {/* Select New CP */}
              <div>
                <div className={cssClasses.sectionLabel}>
                  Select New CP <span style={{ color: "red" }}>*</span>
                </div>
                <div style={{ position: "relative" }}>
                  <ThemeProvider theme={autocompleteTheme}>
                    <Autocomplete
                      options={allCPs.map((cp) => ({
                        label: cp.billingName || cp.cpDisplayId,
                        value: cp.cpId,
                      }))}
                      value={selectedCPName}
                      PaperComponent={({ children }) => (
                        <Paper style={{ fontFamily: "Inter-Medium", fontSize: "1.6rem" }}>
                          {children}
                        </Paper>
                      )}
                      sx={{
                        ".MuiOutlinedInput-root": {
                          background: "#F2F4F7",
                          borderRadius: "8px",
                          "&.Mui-focused": {
                            background: "#FFFFFF",
                            border: "1px solid #84CAFF !important",
                            boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #D1E9FF",
                          },
                        },
                        "& .MuiSvgIcon-root": { display: "none !important" },
                      }}
                      onChange={(_, newValue) => handleCPSelect(newValue)}
                      onInputChange={(_, newInputValue) => setSelectedCPName(newInputValue)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search CP"
                          helperText={cpHelperText}
                          error={cpHelperText !== ""}
                          sx={{
                            fontSize: "1.6rem !important",
                            fontFamily: "Inter-Medium !important",
                            ".MuiFormHelperText-root": {
                              fontSize: "1.4rem !important",
                              fontFamily: "Inter-Medium",
                              marginLeft: "0 !important",
                            },
                          }}
                          InputProps={{ ...params.InputProps, type: "search" }}
                        />
                      )}
                    />
                  </ThemeProvider>
                  {selectedCPName !== "" && (
                    <div style={{ width: "2rem", position: "absolute", top: "2rem", right: "1rem" }}>
                      <img src={SearchIcon} alt="SearchIcon" />
                    </div>
                  )}
                </div>
              </div>

              {/* Select FOS — only if selected CP has FOS */}
              {selectedCPData && allFOS.length > 0 && (
                <div>
                  <div className={cssClasses.sectionLabel}>
                    Select FOS
                  </div>
                  <div style={{ position: "relative" }}>
                    <ThemeProvider theme={autocompleteTheme}>
                      <Autocomplete
                        options={allFOS.map((fos) => ({
                          label: fos.name,
                          value: fos.fosId,
                        }))}
                        value={selectedFOSName}
                        loading={fosLoading}
                        noOptionsText={fosLoading ? "Loading..." : "No FOS found"}
                        PaperComponent={({ children }) => (
                          <Paper style={{ fontFamily: "Inter-Medium", fontSize: "1.6rem" }}>
                            {children}
                          </Paper>
                        )}
                        sx={{
                          ".MuiOutlinedInput-root": {
                            background: "#F2F4F7",
                            borderRadius: "8px",
                            "&.Mui-focused": {
                              background: "#FFFFFF",
                              border: "1px solid #84CAFF !important",
                              boxShadow: "0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #D1E9FF",
                            },
                          },
                          "& .MuiSvgIcon-root": { display: "none !important" },
                        }}
                        onChange={(_, newValue) => {
                          if (newValue) {
                            setFosHelperText("");
                            setSelectedFOSName(newValue.label);
                            const found = allFOS.find((fos) => fos.fosId === newValue.value);
                            setSelectedFOSData(found || null);
                          } else {
                            setSelectedFOSName("");
                            setSelectedFOSData(null);
                          }
                        }}
                        onInputChange={(_, newInputValue) => setSelectedFOSName(newInputValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Search FOS"
                            helperText={fosHelperText}
                            error={fosHelperText !== ""}
                            sx={{
                              fontSize: "1.6rem !important",
                              fontFamily: "Inter-Medium !important",
                              ".MuiFormHelperText-root": {
                                fontSize: "1.4rem !important",
                                fontFamily: "Inter-Medium",
                                marginLeft: "0 !important",
                              },
                            }}
                            InputProps={{ ...params.InputProps, type: "search" }}
                          />
                        )}
                      />
                    </ThemeProvider>
                    {selectedFOSName !== "" && (
                      <div style={{ width: "2rem", position: "absolute", top: "2rem", right: "1rem" }}>
                        <img src={SearchIcon} alt="SearchIcon" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Summary preview — for CP only after CP selected; for Direct/R&L immediately */}
          {selectedLeadType && (!isCP || selectedCPData) && (
            <div className={cssClasses.summaryBox}>
              <div className={cssClasses.summaryTitle}>Lead Card Preview</div>
              <div className={cssClasses.summaryRow}>
                <span className={cssClasses.summaryLabel}>Lead Name</span>
                <span className={cssClasses.summaryValue}>{data?.leadname || "-"}</span>
              </div>
              <div className={cssClasses.summaryRow}>
                <span className={cssClasses.summaryLabel}>Status</span>
                <span className={cssClasses.summaryValue}>{data?.status || "-"}</span>
              </div>
              <div className={cssClasses.summaryRow}>
                <span className={cssClasses.summaryLabel}>Lead Type</span>
                <span className={cssClasses.summaryValueHighlight}>{selectedLeadType}</span>
              </div>
              <div className={cssClasses.summaryRow}>
                <span className={cssClasses.summaryLabel}>CP</span>
                <span className={cssClasses.summaryValue}>
                  {isCP ? (selectedCPData?.billingName || "-") : "None"}
                </span>
              </div>
              <div className={cssClasses.summaryRow}>
                <span className={cssClasses.summaryLabel}>FOS</span>
                <span className={cssClasses.summaryValue}>
                  {isCP ? (selectedFOSData?.name || "-") : "None"}
                </span>
              </div>
              <div className={cssClasses.summaryRow}>
                <span className={cssClasses.summaryLabel}>Sales</span>
                <span className={cssClasses.summaryValue}>{data?.Sales?.name || "-"}</span>
              </div>
            </div>
          )}

          {/* Notes */}
          <div>
            <div className={cssClasses.sectionLabel}>
              Notes <span style={{ color: "red" }}>*</span>
            </div>
            <textarea
              className={cssClasses.notesTextarea}
              placeholder="Add a note..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </>
        )}
        </div>

        {/* Footer */}
        <div className={cssClasses.wrapperFooter}>
        {processing ? (
          <Button
            style={{
              color: "#ffffff",
              backgroundColor: resultIsError ? "#B42318" : "#155EEF",
              fontSize: "1.6rem",
              lineHeight: "2.4rem",
            }}
            btnName={resultMessage ? "Close" : "Please wait..."}
            clicked={() => {
              if (resultMessage) handleClose();
            }}
            disable={!resultMessage}
          />
        ) : (
          <>
          <Button
            style={{
              color: "#344054",
              backgroundColor: "#ffffff",
              fontSize: "1.6rem",
              lineHeight: "2.4rem",
            }}
            btnName="Cancel"
            clicked={handleClose}
          />
          <Button
            style={{
              color: "#ffffff",
              backgroundColor: "#155EEF",
              fontSize: "1.6rem",
              lineHeight: "2.4rem",
            }}
            btnName="Update"
            clicked={handleUpdate}
            disable={
              !selectedLeadType ||
              (isCP && (!selectedCPData || selectedCPData?.cpId === existingCPId)) ||
              (usesGatedFlow && allotmentDetailsLoading)
            }
          />
          </>
        )}
        </div>
      </Box>
    </Modal>
    {showFosConfirm && (
      <ConfirmationAlert
        open={showFosConfirm}
        close={() => setShowFosConfirm(false)}
        confirm={(res) => {
          setShowFosConfirm(false);
          if (res === "confirmed") submitUpdate();
        }}
        title="FOS Not Selected"
        subTitle="You have not selected a FOS for this CP. Do you want to continue without selecting a FOS?"
        buttonTitle="Cancel"
        returnRespButton="Continue"
      />
    )}
    {resultMessage && resultIsError && (
      <ConfirmationAlert
        open={true}
        close={handleClose}
        confirm={handleClose}
        title="Warning"
        subTitle={resultMessage}
        returnRespButton="Close"
      />
    )}
    </>
  );
}
